import { CommonModule, DatePipe } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { LanguageService } from './language.service';
import { MoneyCommaPipe } from './money-comma.pipe';
import { MobileCartService, type CartItem } from './mobile-cart.service';
import { MobileOrdersService } from './mobile-orders.service';
import { MobileSessionService } from './mobile-session.service';
import { MobileProductsService } from './mobile-products.service';

type CartDictionary = {
  title: { en: string; nl: string; fr: string };
  invoiceTitle: { en: string; nl: string; fr: string };
  invoiceHint: { en: string; nl: string; fr: string };
  orderRef: { en: string; nl: string; fr: string };
  issueDate: { en: string; nl: string; fr: string };
  product: { en: string; nl: string; fr: string };
  quantity: { en: string; nl: string; fr: string };
  packSize: { en: string; nl: string; fr: string };
  packIncrementHint: { en: string; nl: string; fr: string };
  unitPrice: { en: string; nl: string; fr: string };
  tax: { en: string; nl: string; fr: string };
  lineTotal: { en: string; nl: string; fr: string };
  subtotal: { en: string; nl: string; fr: string };
  taxAdded: { en: string; nl: string; fr: string };
  grandTotal: { en: string; nl: string; fr: string };
  empty: { en: string; nl: string; fr: string };
  clearCart: { en: string; nl: string; fr: string };
  placeOrder: { en: string; nl: string; fr: string };
  placingOrder: { en: string; nl: string; fr: string };
  placed: { en: string; nl: string; fr: string };
  placeFailed: { en: string; nl: string; fr: string };
  backToProducts: { en: string; nl: string; fr: string };
  remove: { en: string; nl: string; fr: string };
};

const DICTIONARY: CartDictionary = {
  title: { en: 'Cart', nl: 'Winkelwagen', fr: 'Panier' },
  invoiceTitle: { en: 'Draft Order Invoice', nl: 'Conceptbestelling Factuur', fr: 'Facture de Brouillon' },
  invoiceHint: {
    en: 'Review quantities, tax, and totals before placing your order.',
    nl: 'Controleer aantallen, belasting en totalen voordat je bestelt.',
    fr: 'Verifiez les quantites, taxes et totaux avant de commander.',
  },
  orderRef: { en: 'Order Ref', nl: 'Bestelref', fr: 'Ref Commande' },
  issueDate: { en: 'Issue Date', nl: 'Datum', fr: 'Date' },
  product: { en: 'Product', nl: 'Product', fr: 'Produit' },
  quantity: { en: 'Qty', nl: 'Aantal', fr: 'Qte' },
  packSize: { en: 'Pack Size', nl: 'Verpakkingseenheid', fr: 'Taille du Pack' },
  packIncrementHint: {
    en: 'Quantities round up to full packs of',
    nl: 'Aantallen worden naar boven afgerond op volle verpakkingen van',
    fr: 'Les quantites sont arrondies au pack complet de',
  },
  unitPrice: { en: 'Unit Price', nl: 'Stukprijs', fr: 'Prix Unitaire' },
  tax: { en: 'Tax', nl: 'Belasting', fr: 'Taxe' },
  lineTotal: { en: 'Line Total', nl: 'Regeltotaal', fr: 'Total Ligne' },
  subtotal: { en: 'Subtotal', nl: 'Subtotaal', fr: 'Sous-total' },
  taxAdded: { en: 'Tax Added', nl: 'Belasting Toegevoegd', fr: 'Taxe Ajoutee' },
  grandTotal: { en: 'Grand Total', nl: 'Eindtotaal', fr: 'Total General' },
  empty: { en: 'Your cart is empty.', nl: 'Je winkelwagen is leeg.', fr: 'Votre panier est vide.' },
  clearCart: { en: 'Clear Cart', nl: 'Winkelwagen Legen', fr: 'Vider le Panier' },
  placeOrder: { en: 'Place Order', nl: 'Bestelling Plaatsen', fr: 'Passer la Commande' },
  placingOrder: { en: 'Placing Order...', nl: 'Bestelling Plaatsen...', fr: 'Commande en cours...' },
  placed: {
    en: 'Order placed successfully.',
    nl: 'Bestelling succesvol geplaatst.',
    fr: 'Commande placee avec succes.',
  },
  placeFailed: {
    en: 'Failed to place order.',
    nl: 'Bestelling plaatsen is mislukt.',
    fr: 'Echec de la commande.',
  },
  backToProducts: { en: 'Back To Products', nl: 'Terug Naar Producten', fr: 'Retour Aux Produits' },
  remove: { en: 'Remove', nl: 'Verwijderen', fr: 'Supprimer' },
};

@Component({
  selector: 'app-mobile-cart',
  standalone: true,
  imports: [CommonModule, MoneyCommaPipe, DatePipe],
  templateUrl: './mobile-cart.component.html',
  styleUrl: './mobile-cart.component.css',
})
export class MobileCartComponent {
  private readonly router = inject(Router);
  private readonly languageService = inject(LanguageService);
  private readonly sessionService = inject(MobileSessionService);
  private readonly cartService = inject(MobileCartService);
  private readonly productsService = inject(MobileProductsService);
  private readonly ordersService = inject(MobileOrdersService);

  public readonly language = this.languageService.language;
  public readonly cartItems = this.cartService.cartItems;
  public readonly cartRows = computed<CartItem[]>(() =>
    this.cartItems().map((item, index) => ({
      productID: String(item?.productID ?? `row-${index}`),
      productName: String(item?.productName ?? '').trim() || `Item ${index + 1}`,
      packageSize: Math.max(1, Math.floor(Number(item?.packageSize ?? 1) || 1)),
      quantity: Math.max(1, Math.floor(Number(item?.quantity ?? 1))),
      unitPriceCents: Math.max(0, Math.floor(Number(item?.unitPriceCents ?? 0))),
      taxPercentage: Math.max(0, Number(item?.taxPercentage ?? 0)),
    })),
  );
  public readonly cartSubtotalCents = this.cartService.cartSubtotalCents;
  public readonly cartTaxCents = this.cartService.cartTaxCents;
  public readonly cartTotalCents = this.cartService.cartTotalCents;
  public readonly submitted = signal<boolean>(false);
  public readonly placeOrderInFlight = signal<boolean>(false);
  public readonly placeOrderError = signal<boolean>(false);
  public readonly placeOrderMessage = signal<string>('');
  public readonly issueDate = new Date();

  public readonly invoiceRef = computed<string>(() => {
    const day = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    return `BGI-${day}-${String(this.cartRows().length).padStart(2, '0')}`;
  });

  constructor() {
    if (!this.sessionService.isLoggedIn()) {
      void this.router.navigateByUrl('/mobile/login');
      return;
    }

    this.cartService.refreshFromStorage();
    queueMicrotask(() => this.cartService.refreshFromStorage());
    void this.productsService.load();
  }

  public t(key: keyof CartDictionary): string {
    return DICTIONARY[key][this.language()] || DICTIONARY[key].en;
  }

  public goBack(): void {
    void this.router.navigateByUrl('/mobile/dashboard');
  }

  public clearCart(): void {
    this.cartService.clearCart();
    this.submitted.set(false);
    this.placeOrderError.set(false);
    this.placeOrderMessage.set('');
  }

  public removeItem(item: CartItem): void {
    this.cartService.removeItem(item.productID);
    this.submitted.set(false);
    this.placeOrderError.set(false);
    this.placeOrderMessage.set('');
  }

  public setQuantity(item: CartItem, rawValue: string): void {
    this.cartService.setItemQuantity(item.productID, Number(rawValue));
    this.submitted.set(false);
    this.placeOrderError.set(false);
    this.placeOrderMessage.set('');
  }

  public async placeOrder(): Promise<void> {
    if (this.placeOrderInFlight()) {
      return;
    }

    if (this.cartRows().length === 0) {
      this.submitted.set(true);
      this.placeOrderError.set(true);
      this.placeOrderMessage.set(this.t('empty'));
      return;
    }

    this.placeOrderInFlight.set(true);
    this.submitted.set(false);
    this.placeOrderError.set(false);
    this.placeOrderMessage.set('');

    const result = await this.ordersService.placeOrder(this.cartRows(), this.invoiceRef());

    this.placeOrderInFlight.set(false);
    this.submitted.set(true);
    this.placeOrderError.set(!result.ok);
    this.placeOrderMessage.set(result.message || (result.ok ? this.t('placed') : this.t('placeFailed')));
  }

  public lineSubtotal(item: CartItem): number {
    return this.centsToEuro(this.cartService.lineSubtotalCents(item));
  }

  public lineTax(item: CartItem): number {
    return this.centsToEuro(this.cartService.lineTaxCents(item));
  }

  public lineTotal(item: CartItem): number {
    return this.centsToEuro(this.cartService.lineTotalCents(item));
  }

  public subtotal(): number {
    return this.centsToEuro(this.cartSubtotalCents());
  }

  public taxTotal(): number {
    return this.centsToEuro(this.cartTaxCents());
  }

  public grandTotal(): number {
    return this.centsToEuro(this.cartTotalCents());
  }

  public trackByCartProductID(_index: number, item: CartItem): string {
    return item.productID;
  }

  private centsToEuro(cents: number): number {
    return Number(this.productsService.centsToEuro(cents));
  }
}
